"use client";

import styles from "@/styles/account/login.module.scss";
import { useEffect, useState, useRef, useCallback, Suspense } from "react"
import { setCookie } from '@/utils/cookie'
import Image from "next/image";
import Link from "next/link";
import loginApi from '@/service/modules/loginApi'
import { Button, Input, Form, Divider } from 'react-vant'
import { useSearchParams } from 'next/navigation'
import { useDispatch } from 'react-redux'
import { setUserInfo } from '@/store/slices/userSlice'

import { GoogleOAuthProvider } from '@react-oauth/google';
import { useGoogleLoginUtils } from '@/utils/googleLogin'; // 替换为实际的路径
import ReCAPTCHA from 'react-google-recaptcha';
import { matomoSetCustomDimension } from "@/utils/matomoEvent"

import { toast } from '@/components/global/Toast'
import { errorUtils } from "@/utils/error"


// 将登录内容提取到单独的组件中，确保它在GoogleOAuthProvider内部使用
// 创建一个包含 useSearchParams 的组件
function SearchParamsWrapper({ children }) {
  const searchParams = useSearchParams();
  return children(searchParams);
}

function LoginContent() {
  const [form] = Form.useForm()
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const dispatch = useDispatch();

  const [redirectUrl, setRedirectUrl] = useState('')
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState('');
  const [passwordPreview, setPasswordPreview] = useState(false)
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: boolean}>({});

  // 登录、注册成功，返回前页面
  const pageReturn = useCallback(() => {
    if (redirectUrl) {
      window.location.replace(redirectUrl);
    } else {
      window.location.replace('/');
    }
  }, [redirectUrl])

  // 获取重定向地址信息 - 使用 Suspense 包裹 SearchParamsWrapper
  const handleSearchParams = useCallback((searchParams) => {
    const redirect = searchParams.get('redirect')

    // 存贮重定向地址
    if (redirect) {
      setRedirectUrl(redirect)
    }
  }, [])

  // 有token就认定为已经登录，立即跳转回去
  useEffect(() => {
    if (localStorage.getItem('userToken')) {
      pageReturn()
    }
  }, [pageReturn])

  // 获取信用卡支付错误次数以及Recaptcha key
  const getRecaptchaSiteKey = async() => {
    try {
      const res = await loginApi.LoginErrorCheck()
      if (res && res.data) {
        setRecaptchaSiteKey(res.data.siteKey || '')
      }
    } catch (error: unknown) {
      toast.show(errorUtils.getErrorMessage(error));
    }
  }

  // 获取Recaptcha token
  const handleRecaptchaChange = (value: string | null) => {
    console.log('Recaptcha token:', value);
  };

  // 获取Recaptcha token
  useEffect(() => {
    getRecaptchaSiteKey()
  }, [])

  // 去重置密码
  const hendleResetPassword = () => {
    window.location.href = '/reset-password'
  };

  const onFinish = async(values: {email: string; password: string}) => {
    try {
      if (loading) {
        return
      }

      const gRecaptchaResponse = recaptchaRef.current?.getValue();
      if (recaptchaSiteKey && !gRecaptchaResponse) {
        toast.show('Please verify you are not a robot')
        return
      }

      setLoading(true)

      const dataObj = {
        email: values.email.trim(),
        password: window.btoa(values.password),
        recaptcha: ''
      }

      if (recaptchaSiteKey) {
        dataObj.recaptcha = gRecaptchaResponse
      }

      const finnalParams = Object.fromEntries(
        Object.entries(dataObj).filter(([, value]) => value !== null && value !== '')
      );

      const res = await loginApi.login(finnalParams)
      setLoading(false)
      if (res.data) {
        toast.show('Login Successful!');

        // matomo记录登录结果
        const LoginResult = 0
        if (window._paq) {
          window._paq.push(['setUserId', res.data.customerID]);
        }
        matomoSetCustomDimension(21, LoginResult)

        // userEmail存起来，方便数据埋点使用
        if (res.data.email) {
          localStorage.setItem('userEmail', res.data.email || '')
          setCookie('userCustomerId', res.data.customerID || '')
        } else {
          localStorage.removeItem('userEmail')
        }

        // 将用户信息存入Redux
        dispatch(setUserInfo(res.data))

        // 清除所有字段错误状态
        setFieldErrors({});

        // 登录成功，跳转
        setTimeout(() => {
          pageReturn()
        }, 1500);
      }
    } catch (error: unknown) {
      setLoading(false)
      /* 处理错误信息 
        - 假设API错误的返回格式为：{ message: '错误信息', field: '表单字段名' }
        - 你需要根据你实际的错误响应结构来调整
      */

      interface ApiError {
        message: string;
        code: number;
        response?: unknown;
      }
      
      if ((error as ApiError).message) {
        if ((error as ApiError).code == 115001 || (error as ApiError).code == 115002 || (error as ApiError).code == 115004) {
          // 账号或密码输入错误
          const errorMessage = (error as ApiError).message
          const errorField = 'password'

          form.setFields([
            {
              name: errorField,
              errors: [errorMessage],
            }
          ]);
          setFieldErrors(prev => ({ ...prev, [errorField]: true }));
        } else if ((error as ApiError).code == 110000) {
          // captchaCode输入错误
          const errorMessage = (error as ApiError).message
          const errorField = 'captchaCode'

          form.setFields([
            {
              name: errorField,
              errors: [errorMessage],
            }
          ]);
          setFieldErrors(prev => ({ ...prev, [errorField]: true }));
        } else if ((error as ApiError).code == 115003) {
          const currentEmail = localStorage.getItem('userEmail')
          alert(`You are already logged in to your account (${currentEmail}) in another tab.\nPlease log in to the same account in the current tab, or log out of your account (${currentEmail}) and try again.`)

          setTimeout(() => {
            pageReturn()
          }, 500);
        } else {
          // 其他可能的错误，都放在password表单上
          const errorMessage = (error as ApiError).message
          const errorField = 'password'

          form.setFields([
            {
              name: errorField,
              errors: [errorMessage],
            }
          ]);
          setFieldErrors(prev => ({ ...prev, [errorField]: true }));
        }

        // 刷新验证码
        if (
          (error as ApiError).code == 115004 || (error as ApiError).code == 110000) {
          if (recaptchaSiteKey) {
            recaptchaRef.current.reset();
          } else {
            getRecaptchaSiteKey()
          }
        }

      } else {
        // 如果不是一个有效的数据响应，你可以设置一个通用的错误信息
        toast.show('An unexpected error occurred. Please try again.');
      }

      // matomo 记录登录失败信息
      const LoginResult = (error as ApiError).code
      matomoSetCustomDimension(21, LoginResult)
    }
  }

  // 清除字段错误状态
  const clearFieldError = (fieldName: string) => {
    setFieldErrors(prev => ({ ...prev, [fieldName]: false }));
  };

  // Google登录 - 确保在GoogleOAuthProvider内部调用
  const onGoogleLogin = useGoogleLoginUtils({ onSuccessJump: pageReturn });

  return (
    <div className={styles['page-login']}>
      <div className={styles['page-title']}>Sign in</div>

      {/* 使用 Suspense 包裹 SearchParamsWrapper */}
      <Suspense fallback={<div></div>}>
        <SearchParamsWrapper>
          {(searchParams) => {
            // 当 searchParams 可用时调用处理函数
            handleSearchParams(searchParams);
            return null;
          }}
        </SearchParamsWrapper>
      </Suspense>

      <Form
        form={form}
        onFinish={onFinish}
        layout={'vertical'}
        footer={
          <div>
            <div className={styles['forgot-password']} onClick={hendleResetPassword}>
              Forgot password?
            </div>
            <Button 
              round 
              nativeType='submit' 
              type='primary' 
              style={{ fontWeight: '700' }}
              loading={loading}
              loadingText="Sign in"
              block
            >
              Sign in
            </Button>

            <div className={styles['register-tips']}>
              New to Crazysales?&nbsp;
              <Link href='/register' style={{ color: '#319EFF' }}>
                Sign up
              </Link>
            </div>

            <Divider style={{ margin: '6px auto', color: '#333333' }}>OR</Divider>

            <div className={styles['google-login']} onClick={() => onGoogleLogin()}>
              <Image
                alt={'google logo'}
                width={32}
                height={32}
                className={styles['google-icon']}
                src={'https://img.crazysales.com.au/images/Sign-Google.png'} />
              <div className={styles['google-login-text']}>Sign in with Google</div>
            </div>
          </div>
        }
      >
        <Form.Item
          rules={[
            { required: true, message: `Email Address is required` },
            { type: 'email', message: `Invalid Email Address` },
          ]}
          name='email'
          label=''
          className={fieldErrors.email ? 'form-item-error' : ''}
        >
          <Input 
            placeholder='Email address' 
            maxLength={225} 
            onChange={() => clearFieldError('email')}
          />
        </Form.Item>
        <Form.Item
          rules={[
            { required: true, message: `Password is required` },
            { min: 6, message: `Error! Password must be at least 6 characters.` },
          ]}
          name='password'
          label=''
          className={fieldErrors.password ? 'form-item-error' : ''}
        >
          <Input 
            placeholder='Password' 
            type={passwordPreview ? 'text' : 'password'} 
            maxLength={64} 
            onChange={() => clearFieldError('password')}
            suffix={
              <Image
                alt={'google logo'}
                width={20}
                height={20}
                className={styles['preview-icon']}
                src={`${process.env.IMAGE_DOMAIN}/login/${passwordPreview ? 'preview-open.png' : 'preview-close.png'}`}
                onClick={() => setPasswordPreview(!passwordPreview)} />
            }
          />
        </Form.Item>

        {
          !!(recaptchaSiteKey) &&
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={recaptchaSiteKey}
            type={'audio'}
            onChange={handleRecaptchaChange}
          />
        }
      </Form>
    </div>
  )
}

export default function AccountLogin() {
  return (
    <GoogleOAuthProvider clientId={process.env.GOOGLE_CLIENT_ID} >
      <LoginContent />
    </GoogleOAuthProvider>
  )
}